/** How long one flush may run before the remaining tasks are left for the next frame. */
const FRAME_BUDGET_MS = 16;

const tasks = new Set<() => void>();
let handle = 0;

function requestFlush() {
  if (handle) return;

  handle = requestAnimationFrame(() => {
    handle = 0;
    const start = performance.now();

    for (const task of [...tasks]) {
      if (!tasks.delete(task)) continue;

      try {
        task();
      } catch (error) {
        console.error(error);
      }

      if (performance.now() - start > FRAME_BUDGET_MS) break;
    }

    if (tasks.size) requestFlush();
  });
}

/**
 * Runs `task` on the next animation frame, sharing one rAF handle across all callers —
 * thousands of node views can each schedule without registering a frame apiece.
 *
 * A flush stops once it has spent {@link FRAME_BUDGET_MS}; whatever is left stays queued
 * for the following frame, ahead of anything scheduled since.
 *
 * Tasks scheduled from inside a flush run on the following frame, not the current one.
 *
 * @returns a function that unschedules the task.
 */
export function scheduleOnFrame(task: () => void): () => void {
  tasks.add(task);
  requestFlush();

  return () => {
    tasks.delete(task);
  };
}
